import Modal from '@/components/common/modals/Modal';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { logError } from '@/util/logger';
import {
    getNextAtomicProcessingDT,
    getNextTransactionDateForPlan,
} from '@common/atomic/helpers';
import { TransactionScheduleFieldsFragment } from '@common/graphql/generated';
import {
    useGoalAccountContributionLimitStr,
    usePendingWithdrawalAmount,
} from '@common/react/goals';
import { getProcessingDuration } from '@common/react/transactions';
import { TOASTS } from '@common/toasts';
import { dt } from '@common/util/dateTime';
import { useState } from 'react';
import { FrameRenderer } from '../common/SlideAnimator';
import { useToasts } from '../common/Toasts';
import TransactionAmountCard from './TransactionAmountCard';
import TransactionCompleteCard from './TransactionCompleteCard';
import TransactionConfirmationCard from './TransactionConfirmationCard';
import TransactionDepositoryAccountCard from './TransactionDepositoryAccountCard';
import TransactionSchedulingCard from './TransactionSchedulingCard';
import TransactionSlideAnimator from './TransactionSlideAnimator';

interface GoalDepositFlowProps {
    destinationId: string;
    goalId: string;
    existingTransactionSchedule?: TransactionScheduleFieldsFragment;
    onComplete?: () => void;
}

// Atomic requires a deposit to be at least $1
const MIN_AMOUNT_IN_CENTS = 100;

const GoalDepositFlow = ({
    destinationId,
    goalId,
    existingTransactionSchedule,
    onComplete,
}: GoalDepositFlowProps) => {
    useTrackModalShown('Goal Deposit Flow');

    const goalDirection = 'DEPOSIT';
    const modal = useModal();
    const { showErrorToast } = useToasts();

    const [isComplete, setIsComplete] = useState(false);
    const [sourceId, setSourceId] = useState<string | undefined>(
        existingTransactionSchedule?.sourceId
    );
    const [amount, setAmount] = useState(
        existingTransactionSchedule?.amount ?? 0
    );
    const [recurrence, setRecurrence] = useState(
        existingTransactionSchedule?.recurrence
    );
    const nextAtomicProcessingDate =
        getNextAtomicProcessingDT(dt()).toDateStr();
    const [date, setDate] = useState(
        existingTransactionSchedule
            ? getNextTransactionDateForPlan(
                  existingTransactionSchedule
              ).toDateStr()
            : nextAtomicProcessingDate
    );
    const { pendingWithdrawalAmount } = usePendingWithdrawalAmount(sourceId);
    const { contributionLimitStr } =
        useGoalAccountContributionLimitStr(destinationId);

    const onClose = () => {
        modal.remove();
        // Running this here ensures that the onComplete cb is always executed
        // after the flow is complete, even if the user early-exits from the
        // final celebration frame.
        if (isComplete) onComplete?.();
    };

    const schedulingFrame: FrameRenderer = {
        id: 'Scheduling',
        render: ({ goForward }) => {
            return (
                <TransactionSchedulingCard
                    title="How often do you want to repeat this deposit?"
                    subtitle={
                        existingTransactionSchedule
                            ? 'You can update the frequency and date of the next occurrence.'
                            : 'You can schedule this deposit to occur once or repeatedly.'
                    }
                    includeJustOnceOption={!existingTransactionSchedule}
                    recurrence={recurrence}
                    onChangeRecurrence={setRecurrence}
                    date={date}
                    minDate={nextAtomicProcessingDate}
                    onChangeDate={setDate}
                    isNextDisabled={date < dt().toDateStr()}
                    onNext={goForward}
                    onClose={onClose}
                />
            );
        },
    };

    const depositoryAccountFrame: FrameRenderer = {
        id: 'DepositoryAccount',
        render: ({ goBack, goForward }) => {
            return (
                <TransactionDepositoryAccountCard
                    title="Choose source account"
                    subtitle="Select an account to transfer from."
                    goalId={goalId}
                    depositoryAccountId={sourceId}
                    onChangeDepositoryAccountId={setSourceId}
                    onBack={goBack}
                    onNext={goForward}
                    onClose={onClose}
                />
            );
        },
    };

    const amountFrame: FrameRenderer = {
        id: 'Amount',
        render: ({ goBack, goForward }) => {
            if (!sourceId) {
                logError(
                    new Error('sourceId is required to render Amount frame')
                );
                showErrorToast(TOASTS.createTransferUnknownError);
                return <></>;
            }

            const { min, max } = getProcessingDuration(goalDirection);

            return (
                <TransactionAmountCard
                    title="How much do you want to deposit?"
                    subtitle={`It usually takes about ${min} to ${max} days to complete.`}
                    sourceId={sourceId}
                    amount={amount}
                    onChangeAmount={setAmount}
                    pendingWithdrawalAmount={pendingWithdrawalAmount}
                    isCappedAtAvailableBalance={false}
                    minAmount={MIN_AMOUNT_IN_CENTS}
                    contributionLimitStr={contributionLimitStr}
                    onBack={goBack}
                    onNext={goForward}
                    onClose={onClose}
                />
            );
        },
    };

    const confirmationFrame: FrameRenderer = {
        id: 'Confirmation',
        render: ({ goBack, goForward }) => {
            if (!sourceId) return <></>;

            return (
                <TransactionConfirmationCard
                    title={'Please review your deposit details'}
                    nextButtonText={
                        existingTransactionSchedule
                            ? 'Update recurring deposit'
                            : recurrence
                              ? 'Initiate recurring deposit'
                              : 'Initiate deposit'
                    }
                    sourceId={sourceId}
                    destinationId={destinationId}
                    amount={amount}
                    goalDirection={goalDirection}
                    goalId={goalId}
                    pendingWithdrawalAmount={pendingWithdrawalAmount}
                    contributionLimitStr={contributionLimitStr}
                    date={date}
                    recurrence={recurrence}
                    existingTransactionScheduleId={
                        existingTransactionSchedule?.id
                    }
                    onBack={goBack}
                    onCompleted={() => {
                        setIsComplete(true);
                        goForward();
                    }}
                    onClose={onClose}
                />
            );
        },
    };

    const completeFrame: FrameRenderer = {
        id: 'Complete',
        render: () => {
            return (
                <TransactionCompleteCard
                    title={
                        existingTransactionSchedule
                            ? 'Recurring deposit updated!'
                            : recurrence
                              ? 'Recurring deposit initiated!'
                              : 'Deposit initiated!'
                    }
                    onClose={onClose}
                />
            );
        },
    };

    const frames: FrameRenderer[] = [
        schedulingFrame,
        depositoryAccountFrame,
        amountFrame,
        confirmationFrame,
        completeFrame,
    ];

    return (
        <Modal
            size="md"
            isOpen={modal.visible}
            onClose={onClose}
            unstyled
            // This prevents auto-scrolling to first button in select bank account frame
            autoFocus={false}
        >
            <TransactionSlideAnimator frames={frames} />
        </Modal>
    );
};

const GoalDepositFlowModal =
    ModalManager.create<GoalDepositFlowProps>(GoalDepositFlow);

export const showGoalDepositFlowModal = (props: GoalDepositFlowProps) =>
    ModalManager.show(GoalDepositFlowModal, props);
